import { useState } from "react";
import { Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, fileUrl } from "@/lib/api";

const FIELDS = [["sku_prefix", "SKU prefix", "JB"], ["tagline", "Tagline under name", "e.g. Bespoke Studio (optional)"], ["phone", "Phone", "+91 …"], ["email", "Email", "hello@brand.com"], ["website", "Website", "www.brand.com"], ["social", "Instagram / social", "@brand"]];

const LogoField = ({ b, i, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData(); fd.append("files", file);
    try { const { data } = await api.post("/uploads", fd); onChange({ ...b, logo: data[0].path }); toast.success("Logo uploaded, click Save to keep it"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3" data-testid={`brand-logo-${i}`}>
      <div className="w-40 h-12 border border-dashed border-[var(--line)] bg-white flex items-center justify-center overflow-hidden">
        {b.logo ? <img src={fileUrl(b.logo, 400)} alt="logo" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-[var(--muted-ink)] tracking-widest">NO LOGO</span>}
      </div>
      <label className="text-xs underline cursor-pointer">{busy ? "Uploading…" : b.logo ? "Replace logo" : "Upload logo"}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid={`brand-logo-input-${i}`} /></label>
      {b.logo && <button className="text-xs text-[var(--muted-ink)] underline" onClick={() => onChange({ ...b, logo: "" })} data-testid={`brand-logo-remove-${i}`}>Remove</button>}
    </div>
  );
};

const Profile = ({ b, i, isDefault, onChange, onDefault, onRemove }) => (
  <div className="border border-[var(--line)] p-4 space-y-3" data-testid={`brand-profile-${i}`}>
    <div className="flex items-center gap-2">
      <button onClick={onDefault} title={isDefault ? "Default brand" : "Make default"} data-testid={`brand-default-${i}`}>
        <Star size={14} className={isDefault ? "fill-current" : "text-[var(--muted-ink)]"} /></button>
      <Input value={b.name} onChange={(e) => onChange({ ...b, name: e.target.value.toUpperCase() })} className="font-semibold tracking-widest" data-testid={`brand-name-${i}`} />
      {!isDefault && <Button variant="outline" size="sm" onClick={onRemove} data-testid={`brand-remove-${i}`}><Trash2 size={13} /></Button>}
    </div>
    <LogoField b={b} i={i} onChange={onChange} />
    <div className="grid sm:grid-cols-2 gap-3">
      {FIELDS.map(([k, l, ph]) => (
        <div key={k}><label className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-ink)]">{l}</label>
          <Input value={b[k] || ""} placeholder={ph} onChange={(e) => onChange({ ...b, [k]: k === "sku_prefix" ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6) : e.target.value })}
            className="mt-1" data-testid={`brand-${k}-${i}`} /></div>
      ))}
    </div>
  </div>
);

export const BrandListField = ({ brand, setBrand }) => {
  const [t, setT] = useState("");
  const list = brand.brands || [];
  const update = (i, b) => {
    const wasDefault = list[i].name === brand.brand_name;
    setBrand({ ...brand, brands: list.map((x, j) => (j === i ? b : x)), ...(wasDefault && { brand_name: b.name }) });
  };
  const add = () => {
    const v = t.trim().toUpperCase();
    if (!v || list.some((b) => b.name === v)) return;
    setBrand({ ...brand, brands: [...list, { name: v, sku_prefix: v.replace(/[^A-Z0-9]/g, "").slice(0, 2), phone: "", email: "", website: "", social: "" }] });
    setT("");
  };
  return (
    <div className="space-y-4" data-testid="brand-list-field">
      <p className="text-xs text-[var(--muted-ink)]">Each catalog picks one brand in “Edit pages”. Its name, SKU prefix and contact details are used everywhere in that catalog. The starred brand is the default.</p>
      {list.map((b, i) => (
        <Profile key={i} b={b} i={i} isDefault={b.name === brand.brand_name} onChange={(nb) => update(i, nb)}
          onDefault={() => setBrand({ ...brand, brand_name: b.name })} onRemove={() => setBrand({ ...brand, brands: list.filter((_, j) => j !== i) })} />
      ))}
      <div className="flex gap-2">
        <Input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New brand name" data-testid="brand-add-input" />
        <Button variant="outline" onClick={add} data-testid="brand-add-button"><Plus size={14} className="mr-1" />Add brand</Button>
      </div>
    </div>
  );
};
